"use client"

/**
 * InterstitialAd
 *
 * Full-screen overlay ad that appears between user actions.
 * Supports JS embed (ExoClick/TrafficStars interstitial) and banner image fallback.
 *
 * Frequency is controlled by two independent limits (both must pass):
 *   1. posts_between_ads  — show after every N post opens (default 20)
 *   2. cooldown_minutes   — minimum minutes between interstitials (default 15)
 *
 * Both are configurable in Admin → Marketing → Frequency tab.
 * Premium subscribers never see interstitials.
 *
 * ── Creative sizing ──────────────────────────────────────────────────────
 * The js_embed/html_embed branch used to force the iframe to a fixed
 * `width: 100%, height: 300` with no relationship to the zone's actual
 * configured size (1600x900 for this placement — see the ad_zones migration
 * 20261111000100). A 1600x900 creative squeezed into 300px of height (inside
 * a max-w-lg, ~512px-wide card) overflowed in both directions and showed its
 * own internal scrollbar — the same root cause already fixed in
 * NetworkDisplayAd/LetterboxAd/PillarboxAd: render at the creative's true
 * native size and scale the whole frame down to fit, instead of guessing a
 * box and letting the content overflow it.
 */

import { useEffect, useRef, useState, useCallback } from "react"
import { createPortal } from "react-dom"
import { X } from "@/lib/icons"
import { useAdStore } from "@/lib/ad-store"
import { useHasConsent } from "@/components/cookie-consent"
import { useAdTier } from "@/lib/ad-tier"
import { cn } from "@/lib/utils"
import { HouseAd } from "./HouseAd"
import { isAdTestModeOn, isSessionOldEnoughForAds } from "@/lib/ad-cooldown"
import { isGlobalAdsOff } from "@/lib/global-ads-off"
import { extractExoclickZoneId, exoclickIframeSrc } from "@/lib/ad-embed"
import { useTranslations } from "next-intl"

// House-ad and network-ad interstitials used to be one fallback chain (network
// zone, else a house ad) sharing one schedule — reported live as feeling like
// "the same thing" with no independent control, when a house ad is a
// deliberate self-promo the site actually wants seen on its own terms, not a
// leftover shown only when a 3rd-party network happens to have no fill.
// Split into two fully independent triggers below, each with its own
// storage keys, config endpoint, and admin-tunable schedule — "network" and
// "house" never share a counter, cooldown, or session-seen flag.
type InterstitialKind = "network" | "house"

function keysFor(kind: InterstitialKind) {
  const prefix = kind === "house" ? "house_interstitial" : "interstitial"
  return {
    cooldown: `${prefix}_last_seen`,
    postCount: `${prefix}_post_count`,
    shownThisSession: `${prefix}_shown_this_session`,
  }
}

function getCooldownExpired(kind: InterstitialKind, cooldownMinutes: number): boolean {
  if (isAdTestModeOn()) return true
  try {
    const last = localStorage.getItem(keysFor(kind).cooldown)
    if (!last) return true
    return (Date.now() - parseInt(last, 10)) / 60000 >= cooldownMinutes
  } catch { return true }
}

function getShownThisSession(kind: InterstitialKind): boolean {
  if (isAdTestModeOn()) return false
  try {
    return sessionStorage.getItem(keysFor(kind).shownThisSession) === "1"
  } catch { return false }
}

function markShownThisSession(kind: InterstitialKind) {
  try { sessionStorage.setItem(keysFor(kind).shownThisSession, "1") } catch {}
}

function getPostCount(kind: InterstitialKind): number {
  try { return parseInt(localStorage.getItem(keysFor(kind).postCount) ?? "0", 10) } catch { return 0 }
}

function setPostCount(kind: InterstitialKind, n: number) {
  try { localStorage.setItem(keysFor(kind).postCount, String(n)) } catch {}
}

function markCooldown(kind: InterstitialKind) {
  try { localStorage.setItem(keysFor(kind).cooldown, String(Date.now())) } catch {}
}

interface InterstitialConfig {
  enabled: boolean
  posts_between_ads: number   // show after every N post opens
  cooldown_minutes: number    // min minutes between interstitials
  close_after_secs: number    // auto-close after N seconds (0 = manual only)
  show_close_after_secs: number // seconds before X button appears
  min_session_minutes: number // no interstitial at all until the session is this old
}

const NETWORK_DEFAULT_CONFIG: InterstitialConfig = {
  enabled: true,
  posts_between_ads: 20,
  cooldown_minutes: 15,
  close_after_secs: 0,
  show_close_after_secs: 3,
  min_session_minutes: 5,
}

// House ads get a more frequent default schedule than the network default —
// they're the site's own message, not a paid slot to ration as cautiously.
// Still fully admin-tunable independently of the network config above.
const HOUSE_DEFAULT_CONFIG: InterstitialConfig = {
  enabled: true,
  posts_between_ads: 10,
  cooldown_minutes: 10,
  close_after_secs: 0,
  show_close_after_secs: 3,
  min_session_minutes: 5,
}

const CONFIG_ENDPOINT: Record<InterstitialKind, string> = {
  network: "/api/admin/interstitial-config",
  house: "/api/admin/house-interstitial-config",
}
const DEFAULT_CONFIG_FOR: Record<InterstitialKind, InterstitialConfig> = {
  network: NETWORK_DEFAULT_CONFIG,
  house: HOUSE_DEFAULT_CONFIG,
}

// Cached config, one slot per kind so a network fetch/cache can't clobber house's.
const _config: Record<InterstitialKind, InterstitialConfig | null> = { network: null, house: null }
const _configFetch: Record<InterstitialKind, Promise<InterstitialConfig> | null> = { network: null, house: null }

async function fetchConfig(kind: InterstitialKind): Promise<InterstitialConfig> {
  if (_config[kind]) return _config[kind]!
  if (_configFetch[kind]) return _configFetch[kind]!
  _configFetch[kind] = fetch(CONFIG_ENDPOINT[kind])
    .then((r) => r.ok ? r.json() : null)
    .then((d) => {
      const cfg = { ...DEFAULT_CONFIG_FOR[kind], ...(d ?? {}) }
      _config[kind] = cfg
      setTimeout(() => { _config[kind] = null; _configFetch[kind] = null }, 5 * 60 * 1000)
      return cfg
    })
    .catch(() => { _configFetch[kind] = null; return DEFAULT_CONFIG_FOR[kind] })
  return _configFetch[kind]!
}

interface InterstitialAdProps {
  /** Called when the interstitial is dismissed */
  onClose: () => void
  className?: string
  /** "network" fetches a real ad_zones creative (ExoClick/ClickAdilla) and
   * shows nothing if there's no fill — it no longer falls back to a house ad
   * (see the kind-split comment near keysFor() above). "house" skips the
   * network fetch entirely and always renders a self-promo HouseAd. */
  kind?: InterstitialKind
}

interface AdData {
  zone_id: string
  zone_type: string
  embed_code: string | null
  banner_image_url: string | null
  banner_click_url: string | null
  // True native size of the zone's creative (ad_zones.creative_width/height)
  // — see the sizing note above.
  creative_width: number | null
  creative_height: number | null
}

export function InterstitialAd({ onClose, className, kind = "network" }: InterstitialAdProps) {
  const t = useTranslations("interstitialAd")
  const { isPremiumUser } = useAdStore()
  // Force Ads test mode escape hatch — see the matching comment on
  // NetworkDisplayAd.tsx. Admin/super_admin accounts always read
  // isPremiumUser: true, so without this, Force Ads could never actually
  // force this to show for its only realistic tester.
  const effectivePremiumUser = (isPremiumUser || isGlobalAdsOff()) && !isAdTestModeOn()
  const hasConsent = useHasConsent()
  const adTier = useAdTier()
  const [adData, setAdData] = useState<AdData | null>(null)
  // Mirrors adData for the no-fill timer below, which runs inside a `[]`-dep
  // effect closure that would otherwise only ever see adData's initial
  // (null) value, closing a network interstitial that actually did fill in
  // time.
  const adDataRef = useRef<AdData | null>(null)
  const [canClose, setCanClose] = useState(false)
  const [countdown, setCountdown] = useState(0)
  const timerRef = useRef<NodeJS.Timeout | null>(null)
  const impressionId = useRef<string | null>(null)

  // Real card width, so a fixed-size creative can be scaled to fit it — same
  // measure-and-scale approach as the gallery ad slot.
  const [cardWidth, setCardWidth] = useState<number | null>(null)
  const cardRef = useRef<HTMLDivElement | null>(null)
  const setCardRef = useCallback((node: HTMLDivElement | null) => {
    cardRef.current = node
    if (node) setCardWidth(node.clientWidth)
  }, [])
  useEffect(() => {
    const el = cardRef.current
    if (!el || typeof ResizeObserver === "undefined") return
    const ro = new ResizeObserver(() => setCardWidth(el.clientWidth))
    ro.observe(el)
    return () => ro.disconnect()
  }, [adData])

  useEffect(() => {
    if (effectivePremiumUser || !hasConsent) { onClose(); return }

    let noFillTimer: NodeJS.Timeout | null = null
    let ignore = false

    if (kind === "network") {
      // Fetch ad zone. In Force Ads test mode, ask for preview=1 as well: every
      // interstitial zone is currently is_active=false (deliberately — the
      // creatives being served were poor), so without this the placement can
      // never be exercised at all, even by an admin trying to confirm it fires.
      // preview=1 is validated server-side against the caller's role, so a
      // non-admin sending it gets nothing.
      //
      // `ignore` guards against the same bug class fixed in HouseAd.tsx:
      // this effect has no cancellation guard, so React Strict Mode's
      // dev-only double-invoke fires this fetch twice on mount. For the
      // house-ad path that just meant a visible ad swap; here it's worse --
      // each successful fetch also fires a separate impression-tracking
      // POST below, so the uncancelled duplicate was double-counting
      // impressions, not just flickering the creative.
      fetch(`/api/ads/network-grid-ad?type=interstitial&tier=${adTier}${isAdTestModeOn() ? "&preview=1" : ""}`)
        .then((r) => r.ok ? r.json() : null)
        .then((d) => {
          if (ignore) return
          if (d?.zone_id) {
            adDataRef.current = d
            setAdData(d)
            fetch("/api/ads/zone-interact", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ zone_id: d.zone_id, event: "impression" }),
            })
              .then((r) => (r.ok ? r.json() : null))
              .then((r) => { if (r?.impression_id) impressionId.current = r.impression_id })
              .catch(() => {})
          }
        })
        .catch(() => {})

      // No fallback to a house ad on no-fill anymore (see the kind-split
      // comment near keysFor()) — a network interstitial with nothing to show
      // just closes itself instead of leaving an empty full-screen backdrop
      // up. 1.5s gives the fetch above a real chance to resolve first.
      noFillTimer = setTimeout(() => {
        if (!adDataRef.current) onClose()
      }, 1500)
    }
    // kind === "house": no zone fetch at all — the render below goes
    // straight to <HouseAd>.

    // Fetch config for close timing
    fetchConfig(kind).then((cfg) => {
      const showAfter = cfg.show_close_after_secs ?? 3
      setCountdown(showAfter)

      if (showAfter > 0) {
        let remaining = showAfter
        timerRef.current = setInterval(() => {
          remaining -= 1
          setCountdown(remaining)
          if (remaining <= 0) {
            clearInterval(timerRef.current!)
            setCanClose(true)
          }
        }, 1000)
      } else {
        setCanClose(true)
      }

      // Auto-close
      if (cfg.close_after_secs > 0) {
        setTimeout(() => { onClose() }, cfg.close_after_secs * 1000)
      }
    })

    markCooldown(kind)
    setPostCount(kind, 0)
    markShownThisSession(kind)

    return () => {
      ignore = true
      if (timerRef.current) clearInterval(timerRef.current)
      if (noFillTimer) clearTimeout(noFillTimer)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // JS embed rendering: see the sandboxed-iframe note on the js_embed
  // branch below. This used to inject the network's script directly into
  // this document, which is exactly what let a real ExoClick display
  // script (same network, same ad-provider.js loader) hijack clicks
  // sitewide — reported live: closing this very interstitial stopped
  // working, landing back on the grid instead of dismissing, while the
  // console filled with hundreds of stacked "Cannot read properties of
  // null (reading 'length')" errors from a.magsrv.com/ad-provider.js. A
  // same-origin script embedded in the live page can attach a listener
  // anywhere in the document, including on top of our own close button; a
  // sandboxed iframe cannot reach outside its own frame no matter how the
  // ad network's script behaves.

  if (effectivePremiumUser || !hasConsent) return null
  if (typeof document === "undefined") return null

  const isEmbed = adData?.zone_type === "js_embed" || adData?.zone_type === "html_embed"
  const hasSizedEmbed = isEmbed && !!adData?.creative_width && !!adData?.creative_height
  const embedZoneId = isEmbed ? extractExoclickZoneId(adData!.embed_code) : null

  // Networks other than ExoClick (ClickAdilla's `<script data-admpid>` tag is
  // the one actually in use — see the network-grid-ad response's "network"
  // field) don't give us a recognizable zone ID or a configured creative
  // size, and they have no equivalent of ExoClick's magsrv.com iframe.php
  // URL to point a real cross-origin `src=` at (see lib/ad-embed.ts for why
  // that trick is what lets ExoClick's script work at all despite the
  // sandbox). Their script has to run via `srcDoc` in an opaque-origin
  // frame instead, where storage/cookie APIs it depends on throw — reported
  // live as a self-styled "Anti Malware Update" creative rendering inside a
  // solid black box with console errors ("detectIncognito... failed to
  // query storage quota", "CMP not found").
  //
  // Building this network a genuine non-opaque origin (the ExoClick-style
  // fix) would need a loader page hosted on a real separate domain — actual
  // infrastructure, not something to stand up unilaterally from here. This
  // is the deliberate stopgap instead: the sandbox stays exactly as safe as
  // it is today (unchanged), and only the CONTAINER around it changes, so
  // whatever the script manages to render despite its partial failures
  // isn't fighting a solid black card sized for a 1600x900 ExoClick
  // creative it was never given.
  const isUnsizedNonExoclickEmbed = isEmbed && !hasSizedEmbed && !embedZoneId

  // Portaled to document.body, and pointer-events-auto, to fix two bugs
  // hit specifically when this renders inside booru-detail-modal.tsx's
  // dialog (formerly duplicated in a since-removed sibling component,
  // BooruModalAdWrapper, which independently tracked the same interstitial
  // frequency and raced with this hook's counters — see recordPostOpen()):
  //
  //  1. booru-detail-modal renders <InterstitialAd> INSIDE its own Radix
  //     Dialog tree. A plain position:fixed div there can paint above the
  //     modal yet still lose the hit test to the modal's portaled content,
  //     so document.elementFromPoint() over the close button resolves to the
  //     underlying post <img> instead of the button.
  //  2. Radix Dialog's scroll-lock sets pointer-events:none on body-level
  //     content that isn't its own marked portal while the dialog is open,
  //     and this subtree inherited it.
  //
  // Net effect, reported live: the ad appeared over a gallery and the close
  // button, backdrop click, and Escape were all completely dead.
  return createPortal(
    <div
      // Marks this subtree for any Radix Dialog rendered underneath (see
      // booru-detail-modal.tsx's onInteractOutside) — this is a raw
      // createPortal, not Radix's own Dialog.Portal, so a click here reads
      // as "outside" to any open Radix Dialog's dismiss-on-outside-click
      // detection. Reported live: clicking this ad's own close button (or
      // its backdrop) closed the underlying gallery too, on top of just
      // dismissing the ad — both handlers fired off the same click.
      data-interstitial-ad-portal
      className={cn(
        "pointer-events-auto fixed inset-0 z-[9999] flex items-center justify-center",
        "bg-black/85 backdrop-blur-sm",
        className
      )}
      onClick={(e) => { if (e.target === e.currentTarget && canClose) onClose() }}
    >
      <div
        ref={setCardRef}
        className={cn(
          "relative w-full max-h-[90vh] mx-4 rounded-2xl overflow-y-auto shadow-2xl",
          // A solid black card behind a properly-sized creative (ExoClick,
          // house ad, banner image) is the intended "movie-break" look. For
          // an unsized non-ExoClick embed — see isUnsizedNonExoclickEmbed
          // above — the card doesn't know the creative's real shape, so a
          // solid black box just reads as broken dead space around whatever
          // small/undersized thing the script manages to render. Transparent
          // lets the backdrop's own blur/dim show through instead, and the
          // creative (or its absence) reads as an overlay, not a hole.
          isUnsizedNonExoclickEmbed ? "bg-transparent" : "bg-black",
          // HouseAd/banner-image fallback is portrait-ish and was sized for
          // max-w-lg; a real network embed's creative (1600x900 for this
          // placement) needs real room or it scales down to nothing.
          hasSizedEmbed ? "max-w-3xl" : "max-w-lg",
        )}
      >
        {/* Close button */}
        <button
          onClick={canClose ? onClose : undefined}
          className={cn(
            // bottom-right, not top-right: ExoClick creatives commonly paint
            // their own close button at their box's top-right (see
            // StickyBannerAd.tsx's comment on the same collision) — this
            // placement was never moved when the other four sitewide ad
            // widgets were, so it kept the exact bug being "fixed" elsewhere.
            "absolute bottom-3 right-3 z-10 h-8 w-8 rounded-full flex items-center justify-center transition-all",
            canClose
              ? "bg-primary hover:bg-primary/90 text-primary-foreground ring-2 ring-white/90 shadow-md cursor-pointer"
              : "bg-black/40 text-white/40 cursor-not-allowed"
          )}
          aria-label={canClose ? t("closeAd") : t("closeInS", { seconds: countdown })}
        >
          {canClose ? (
            <X className="h-4 w-4" />
          ) : (
            <span className="text-xs font-bold tabular-nums">{countdown}</span>
          )}
        </button>

        {/* Ad label */}
        <div className="absolute top-3 left-3 z-10">
          <span className="text-[10px] bg-black/50 text-white/60 px-2 py-0.5 rounded-full backdrop-blur-sm">
            {t("advertisement")}
          </span>
        </div>

        {/* Ad content */}
        <div
          className={cn(
            "flex items-center justify-center",
            // A ClickAdilla-style creative is typically a small, self-sized
            // notification widget, not a full 1600x900 interstitial — a
            // 300px reservation here is exactly the guessed empty space
            // isUnsizedNonExoclickEmbed exists to stop. Only the real,
            // properly-sized fallback (a network we don't yet have a zone
            // ID for, but which DOES report a creative size) keeps the
            // reservation.
            !hasSizedEmbed && !isUnsizedNonExoclickEmbed && "min-h-[300px]"
          )}
        >
          {isEmbed ? (() => {
            const zoneId = embedZoneId

            if (hasSizedEmbed) {
              const nativeW = adData!.creative_width as number
              const nativeH = adData!.creative_height as number
              // Never upscale a fixed-size creative — a 1600x900 stretched
              // past its own resolution just looks blurry.
              const scale = cardWidth ? Math.min(1, cardWidth / nativeW) : 1
              const scaledH = nativeH * scale
              const style = {
                width: nativeW,
                height: nativeH,
                border: 0,
                display: "block" as const,
                transform: `scale(${scale})`,
                transformOrigin: "top left",
              }
              return (
                <div style={{ width: cardWidth ? Math.min(cardWidth, nativeW * scale) : nativeW, height: Math.round(scaledH), overflow: "hidden", position: "relative" }}>
                  {zoneId ? (
                    <iframe src={exoclickIframeSrc(zoneId)} sandbox="allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox" title={t("advertisement")} scrolling="no" style={style} />
                  ) : (
                    <iframe
                      srcDoc={`<!DOCTYPE html><html><head><style>html,body{margin:0;padding:0;overflow:hidden;background:transparent}</style></head><body>${adData!.embed_code ?? ""}</body></html>`}
                      sandbox="allow-scripts"
                      title={t("advertisement")}
                      scrolling="no"
                      style={style}
                    />
                  )}
                </div>
              )
            }

            // Zone has no configured size — fall back to the previous
            // full-width behavior rather than guessing a height.
            const style = { width: "100%", height: 300, border: 0, display: "block" as const }
            // See lib/ad-embed.ts — srcDoc+allow-scripts-only gives the frame
            // an opaque origin, which blocks ExoClick's cookie write and the
            // creative never renders.
            return zoneId ? (
              <iframe src={exoclickIframeSrc(zoneId)} sandbox="allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox" title={t("advertisement")} scrolling="no" style={style} />
            ) : (
              // No recognized ExoClick zone ID — this is where a
              // ClickAdilla-style script tag lands (see
              // isUnsizedNonExoclickEmbed above for the full story).
              //
              // Still a guessed box, honestly — an iframe's CSS width/height
              // cannot auto-size to CROSS-ORIGIN content the way a normal
              // element sizes to its children; without the ad's cooperation
              // (a postMessage protocol it doesn't speak) or a real origin
              // to measure from (see the module doc comment on why that's
              // infrastructure, not a code fix), there is no way to learn
              // its true size from here. What changed from the old guess:
              // 512x300 assumed something close to a full interstitial
              // creative; this network's actual output looks like the same
              // small notification-style format as InstantMessageAd/
              // InPagePushAd elsewhere in this codebase, so the guess now
              // matches THAT shape instead. Combined with the transparent
              // card above, an oversized guess here at least no longer
              // paints as a solid black hole — it's empty transparent
              // backdrop, not a dead rectangle.
              //
              // Sandbox gains allow-popups/allow-popups-to-escape-sandbox,
              // matching the pattern already proven safe for this exact
              // script family in popunder-injector.tsx — still no
              // allow-same-origin, so this stays exactly as contained as it
              // is today.
              <iframe
                srcDoc={`<!DOCTYPE html><html><head><style>html,body{margin:0;padding:0;overflow:hidden;background:transparent}</style></head><body>${adData!.embed_code ?? ""}</body></html>`}
                sandbox="allow-scripts allow-popups allow-popups-to-escape-sandbox"
                title={t("advertisement")}
                scrolling="no"
                style={{ width: 340, height: 220, maxWidth: "100%", border: 0, display: "block" }}
              />
            )
          })() : adData?.zone_type === "banner_image" && adData.banner_image_url ? (
            <a
              href={adData.banner_click_url ?? "#"}
              target="_blank"
              rel="noopener noreferrer"
              className="block w-full"
              onClick={() => {
                fetch("/api/ads/zone-interact", {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({
                    zone_id: adData.zone_id,
                    event: "click",
                    impression_id: impressionId.current,
                  }),
                }).catch(() => {})
              }}
            >
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img src={adData.banner_image_url} alt={t("advertisement")} className="w-full h-auto" />
            </a>
          ) : kind === "house" ? (
            <HouseAd onClose={onClose} />
          ) : null /* network, no fill yet — the no-fill timer above closes this shortly */}
        </div>
      </div>
    </div>,
    document.body
  )
}

/**
 * Shared trigger logic behind useInterstitial()/useHouseInterstitial() below.
 * Each kind gets its own config, cooldown, post-count, and once-per-session
 * flag (see keysFor()) — they never share a schedule, so a house ad firing
 * doesn't consume the network's counter or vice versa.
 *
 * recordPostOpen() returns whether it just triggered, synchronously — this
 * lets a caller juggling both hooks (booru-detail-modal.tsx) give house ads
 * priority on a post open where both would have fired, without needing to
 * wait a render cycle to read the resulting state back.
 */
function useInterstitialTrigger(kind: InterstitialKind) {
  const { isPremiumUser } = useAdStore()
  const hasConsent = useHasConsent()
  const [showInterstitial, setShowInterstitial] = useState(false)
  const [config, setConfig] = useState<InterstitialConfig>(DEFAULT_CONFIG_FOR[kind])

  useEffect(() => {
    fetchConfig(kind).then(setConfig)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const recordPostOpen = (): boolean => {
    // Force Ads test mode escape hatch — see the matching comment on
    // NetworkDisplayAd.tsx. Without this, an admin/super_admin account
    // (always isPremiumUser: true) returned here before ever reaching the
    // isAdTestModeOn() bypass two lines down, making that bypass dead code
    // for its only realistic tester.
    if (((isPremiumUser || isGlobalAdsOff()) && !isAdTestModeOn()) || !hasConsent || !config.enabled) return false

    if (isAdTestModeOn()) {
      setShowInterstitial(true)
      return true
    }

    // Hard "once per session" guarantee — see the key comment above. The
    // cooldown alone (now 24h by default) makes a second show within one
    // sitting very unlikely, but doesn't rule it out for a session left open
    // across days; this does.
    if (getShownThisSession(kind)) return false

    const count = getPostCount(kind) + 1
    setPostCount(kind, count)

    if (
      count >= config.posts_between_ads &&
      getCooldownExpired(kind, config.cooldown_minutes) &&
      isSessionOldEnoughForAds(config.min_session_minutes)
    ) {
      setShowInterstitial(true)
      return true
    }
    return false
  }

  const dismissInterstitial = () => setShowInterstitial(false)

  return { showInterstitial, recordPostOpen, dismissInterstitial }
}

/**
 * Hook to manage NETWORK (ExoClick/ClickAdilla) interstitial triggering.
 * Call `recordPostOpen()` every time a user opens a post.
 * Returns `showInterstitial` (boolean) and `dismissInterstitial()`.
 */
export function useInterstitial() {
  return useInterstitialTrigger("network")
}

/**
 * Hook to manage the site's own HOUSE AD interstitial triggering — fully
 * independent schedule from useInterstitial() above (see the kind-split
 * comment near keysFor()). Same shape, so it's a drop-in alongside the
 * network hook.
 */
export function useHouseInterstitial() {
  return useInterstitialTrigger("house")
}
